import { useState } from 'react';
import { formatDate, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Panel from '../Panel';
import defaultProfileImg from '~/images/HHLDefault-01.jpg';
import StatusDrawer from './drawers/StatusDrawer';
import WPStatusModal from './modals/WPStatusModal';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { getCampaignStatusBadgeClass } from '~/utils/campaignBadgeStyles';
import { publishCampaignToWP, setCampaignWPStatus } from '~/services/campaign';
import { toast } from 'react-hot-toast';

interface CampaignStatus {
    id?: number;
    code?: string;
    name?: string;
}

interface Props {
    campaign?: any;
    campaignPhotoInfo?: any;
    application?: any;
    onStatusUpdate?: (newStatus: CampaignStatus) => void;
    onRefresh?: () => Promise<any>;
    onPublishSuccess?: (resp: { ID?: number | string; permalink?: string } | null | undefined) => void;
}

const TitlePanel: React.FC<Props> = ({ campaign, campaignPhotoInfo, application, onStatusUpdate, onRefresh, onPublishSuccess }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isWPModalOpen, setIsWPModalOpen] = useState(false);
    const [isWPLoading, setIsWPLoading] = useState(false);
    const navigate = useNavigate();

    const handleWPStatusChange = async (wpStatus: 'publish' | 'draft') => {
        const campaignId = campaign?.campaign_id || campaign?.id;
        if (!campaignId) return;

        setIsWPLoading(true);
        try {
            await setCampaignWPStatus(campaignId, wpStatus);
            toast.success(
                wpStatus === 'publish'
                    ? 'Campaign published on website'
                    : 'Campaign unpublished from website'
            );
            setIsWPModalOpen(false);
            // Re-fetch so the visibility icon reflects the new privacy_flag
            // without requiring a browser refresh
            await onRefresh?.();
        } catch (error) {
            console.error('Error updating WP status:', error);
            toast.error(
                error instanceof Error ? error.message : 'Failed to update website status'
            );
        } finally {
            setIsWPLoading(false);
        }
    };

    const handlePublishToWP = async () => {
        const campaignId = campaign?.campaign_id || campaign?.id;
        if (!campaignId) return;

        setIsWPLoading(true);
        try {
            const resp = await publishCampaignToWP(campaignId);
            toast.success('Campaign published to WordPress!');
            onPublishSuccess?.(resp);
            await onRefresh?.();
        } catch (error) {
            console.error('Error publishing to WordPress:', error);
            toast.error(
                error instanceof Error ? error.message : 'Failed to publish campaign'
            );
        } finally {
            setIsWPLoading(false);
        }
    };

    const photoInfo = campaignPhotoInfo;

    // Normalize campaign status to support both string and object responses
    const statusName: string | undefined = (() => {
        const cs = campaign?.campaign_status;
        if (!cs) return undefined;
        return typeof cs === 'string' ? cs : cs?.name;
    })();

    const statusCode: string | undefined = (() => {
        const cs = campaign?.campaign_status;
        if (!cs) return undefined;
        return typeof cs === 'string' ? cs.toLowerCase() : cs?.code;
    })();

    const isDisbursableOrFunded = statusCode === 'disbursable' || statusCode === 'funded';

    const wordpressPermalink = campaign?.wordpress_permalink;
    const hasValidWordpressPermalink =
        typeof wordpressPermalink === 'string' && wordpressPermalink.startsWith('http');
    // Campaign opens /campaign/<id>, which WordPress redirects to the page.
    // It stays usable for any campaign that has a page, not just open ones.
    const canViewCampaign = isDisbursableOrFunded || hasValidWordpressPermalink;

    return (
        <div className='mb-7.5'>
            <Panel>
                <div className='xl:flex xl:justify-between'>
                    <div className='mb-6 flex gap-[16px] xl:mb-0'>
                        <img
                            className='h-[80px] w-[80px] flex-initial rounded-full object-cover'
                            src={
                                photoInfo?.photo_thumb_url ||
                                photoInfo?.photo_url ||
                                defaultProfileImg
                            }
                        />
                        <div>
                            <div className='flex items-center gap-[16px]'>
                                <h1 className='text-[38px]/[44px] font-medium'>
                                    {campaign?.full_name}
                                </h1>
                                <button
                                    onClick={() => setIsWPModalOpen(true)}
                                    className={`${campaign?.privacy_flag ? 'text-gray-400 hover:text-gray-600' : 'text-primary hover:text-primary/70'} transition-colors`}
                                    title={campaign?.privacy_flag ? 'Campaign is private' : 'Campaign is public'}
                                >
                                    {campaign?.privacy_flag ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                                </button>
                                <div
                                    className={`inline-flex rounded-full px-2 py-1 text-sm ${getCampaignStatusBadgeClass(statusName)}`}
                                >
                                    {statusName ?? 'Unknown'}
                                </div>
                            </div>
                            <div className='mt-2 flex items-center gap-[16px]'>
                                <div className='text-[16px]/[24px] text-[#949599]'>
                                    {campaign?.ntaf_id}
                                </div>
                                <div className='text-[16px]/[24px] text-[#949599]'>
                                    Last Updated:{' '}
                                    {campaign?.update_date &&
                                        formatDate(
                                            parseISO(campaign?.update_date),
                                            'MM/dd/yy'
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='flex w-full flex-col gap-3 xl:w-auto xl:items-end'>
                        <div className='flex flex-wrap items-start gap-4 xl:flex-nowrap'>
                            <button
                                className='py-[7px] text-[14px]/[18px] font-bold uppercase text-primary hover:bg-opacity-90 whitespace-nowrap'
                                onClick={() => {
                                    setIsDrawerOpen(true);
                                }}
                            >
                                Update Status
                            </button>
                            {application?.id && (
                                <button
                                    onClick={() => navigate(`/campaign-applications/${application.id}`)}
                                    className='rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:border-primary hover:bg-white hover:text-primary whitespace-nowrap'
                                >
                                    Application
                                </button>
                            )}
                            <button 
                                onClick={() =>
                                    window.open(
                                        `${import.meta.env.VITE_CLIENT_URL}/dashboard?campaignId=${
                                            campaign?.campaign_id
                                        }`,
                                        '_blank'
                                    )
                                }
                                className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white whitespace-nowrap'>
                                Dashboard
                            </button>
                            <button
                                onClick={() =>
                                    window.open(
                                        `${import.meta.env.VITE_WP_URL}/campaign/${
                                            campaign?.campaign_id
                                        }`,
                                        '_blank'
                                    )
                                }
                                className={`rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 whitespace-nowrap ${
                                    !canViewCampaign
                                        ? 'border-[#F4F4F5] bg-[#F4F4F5] text-[#949599]'
                                        : 'border-warning bg-warning text-black hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                }`}
                                disabled={!canViewCampaign}
                            >
                                Campaign
                            </button>
                        </div>
                        {!hasValidWordpressPermalink && isDisbursableOrFunded ? (
                            <button
                                type='button'
                                onClick={handlePublishToWP}
                                disabled={isWPLoading}
                                className='rounded-full border-[2px] border-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-primary transition-colors duration-500 hover:bg-primary hover:text-white whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                                {isWPLoading ? 'Publishing...' : 'Publish'}
                            </button>
                        ) : null}
                    </div>
                </div>
            </Panel>
            <StatusDrawer
                isOpen={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
                campaign={campaign}
                onStatusUpdate={onStatusUpdate}
            />
            <WPStatusModal
                isOpen={isWPModalOpen}
                onClose={() => setIsWPModalOpen(false)}
                onConfirm={handleWPStatusChange}
                isLoading={isWPLoading}
                campaignName={campaign?.full_name}
            />
        </div>
    );
};

export default TitlePanel;
